import { PeerConnection } from '../types';

export class WebRTCService {
  private peers: Map<string, PeerConnection> = new Map();
  private webSocketService: any = null;
  private sessionCode: string | null = null;
  
  public onMessage: ((data: any, fromPeerId: string) => void) | null = null;

  constructor() {
    this.setupConfiguration();
  }

  private setupConfiguration() {
    // WebRTC configuration will be set up here
  }

  public setWebSocketService(service: any) {
    this.webSocketService = service;
  }

  public setSessionCode(code: string) {
    this.sessionCode = code;
  }

  public async handlePeerJoined(peerId: string) {
    const peerConnection = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    });

    // Create data channel
    const dataChannel = peerConnection.createDataChannel('clipboard', {
      ordered: true
    });

    this.setupDataChannel(dataChannel, peerId);

    // Handle incoming data channel
    peerConnection.ondatachannel = (event) => {
      const channel = event.channel;
      this.setupDataChannel(channel, peerId);
    };

    // Handle ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.webSocketService) {
        this.webSocketService.send({
          type: 'webrtc_ice',
          to: peerId,
          candidate: event.candidate,
          sessionCode: this.sessionCode
        });
      }
    };

    this.peers.set(peerId, {
      connection: peerConnection,
      dataChannel
    });

    // Create offer
    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);

    if (this.webSocketService) {
      this.webSocketService.send({
        type: 'webrtc_offer',
        to: peerId,
        offer: offer,
        sessionCode: this.sessionCode
      });
    }
  }

  public async handleOffer(peerId: string, offer: RTCSessionDescriptionInit) {
    const peerConnection = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    });

    // Handle incoming data channel
    peerConnection.ondatachannel = (event) => {
      const channel = event.channel;
      this.setupDataChannel(channel, peerId);
      
      this.peers.set(peerId, {
        connection: peerConnection,
        dataChannel: channel
      });
    };

    // Handle ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.webSocketService) {
        this.webSocketService.send({
          type: 'webrtc_ice',
          to: peerId,
          candidate: event.candidate,
          sessionCode: this.sessionCode
        });
      }
    };

    await peerConnection.setRemoteDescription(offer);
    const answer = await peerConnection.createAnswer();
    await peerConnection.setLocalDescription(answer);

    if (this.webSocketService) {
      this.webSocketService.send({
        type: 'webrtc_answer',
        to: peerId,
        answer: answer,
        sessionCode: this.sessionCode
      });
    }
  }

  public async handleAnswer(peerId: string, answer: RTCSessionDescriptionInit) {
    const peer = this.peers.get(peerId);
    if (peer?.connection) {
      await peer.connection.setRemoteDescription(answer);
    }
  }

  public async handleIceCandidate(peerId: string, candidate: RTCIceCandidateInit) {
    const peer = this.peers.get(peerId);
    if (peer?.connection) {
      await peer.connection.addIceCandidate(candidate);
    }
  }

  public handlePeerLeft(peerId: string) {
    const peer = this.peers.get(peerId);
    if (peer) {
      peer.connection.close();
      peer.dataChannel?.close();
      this.peers.delete(peerId);
    }
  }

  private setupDataChannel(channel: RTCDataChannel, peerId: string) {
    channel.onopen = () => {
      console.log(`Data channel with ${peerId} opened`);
    };

    channel.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (this.onMessage) {
          this.onMessage(data, peerId);
        }
      } catch (err) {
        console.error('Error parsing message:', err);
      }
    };

    channel.onclose = () => {
      console.log(`Data channel with ${peerId} closed`);
    };

    channel.onerror = (error) => {
      console.error(`Data channel error with ${peerId}:`, error);
    };
  }

  public broadcast(data: { type: 'text' | 'image'; content: string }) {
    const message = JSON.stringify(data);
    let sentCount = 0;
    
    this.peers.forEach((peer, peerId) => {
      if (peer.dataChannel && peer.dataChannel.readyState === 'open') {
        try {
          peer.dataChannel.send(message);
          sentCount++;
        } catch (err) {
          console.error(`Failed to send message to ${peerId}:`, err);
        }
      }
    });
    
    console.log(`Message broadcasted to ${sentCount} peers`);
  }

  public cleanup() {
    this.peers.forEach((peer) => {
      peer.connection.close();
      peer.dataChannel?.close();
    });
    this.peers.clear();
  }
}